import { useCurrencyContext } from '@shared/utils/currency';
import { PriceConfig, CardPriceModifiers } from '../routes/CardCustomizer/CardCustomizer.types';

const _minorToMajorUnits = (val: number) => val / 100;

const useCalculatePrintOrderPrice = (priceUpTo: 'cardFront' | 'cardBack' | 'envelope' | 'total', config: PriceConfig, customizations: CardPriceModifiers) => {
  const quantity = customizations.quantity;
  const currency = useCurrencyContext();

  // Configs contain additional formatting helpers
  // The following 3 fields are expected to always be present:
  //  - quantity (customizations -> quantity)
  //  - shape (customizations -> shape)
  //  - return address (card -> envelope -> layout)
  const quantityConfig = config.quantity.saveTheDate[quantity];
  const dieCutConfig = config.shape[customizations.shape];
  const returnAddressConfig = config.returnAddress[customizations.returnAddress];

  // ==============================
  // Prices in minor units
  // ==============================
  const totalPriceInMinorUnitsPerQuantity = quantityConfig.totalPriceInMinorUnits;
  const totalPricePerCardShapeInMinorUnits = dieCutConfig.getTotalPriceByQuantityInMinorUnits(quantity);
  const totalReturnAddressPriceInMinorUnits = returnAddressConfig.getTotalPriceByQuantityInMinorUnits(quantity);
  // customizations.delivery is not automatically set, user has to make the selection
  const totalDeliveryPriceInMinorUnits = customizations.delivery ? config.delivery[customizations.delivery] : 0;

  // Static prices in minor units (not configurable in MVP)
  const totalPaperTypePriceInMinorUnits = 0;
  const totalPaperSizePriceInMinorUnits = 0;
  const totalBackLayoutPriceInMinorUnits = 0;
  const totalEnvelopesPriceInMinorUnits = 0;

  // ==============================
  // Calculating total prices
  // ==============================
  let runningTotalInMinorUnits = 0;

  // Totals for each configurable step
  const cardFrontTotalPrice = totalPaperTypePriceInMinorUnits + totalPaperSizePriceInMinorUnits + totalPricePerCardShapeInMinorUnits + totalPriceInMinorUnitsPerQuantity;
  const cardBackTotalPrice = totalBackLayoutPriceInMinorUnits;
  const envelopeTotalPrice = totalReturnAddressPriceInMinorUnits;

  // Calculating running total based
  if (priceUpTo === 'cardFront') {
    runningTotalInMinorUnits += cardFrontTotalPrice;
  } else if (priceUpTo === 'cardBack') {
    runningTotalInMinorUnits += cardFrontTotalPrice + cardBackTotalPrice;
  } else if (priceUpTo === 'envelope') {
    runningTotalInMinorUnits += cardFrontTotalPrice + cardBackTotalPrice + envelopeTotalPrice;
  } else if (priceUpTo === 'total') {
    runningTotalInMinorUnits += cardFrontTotalPrice + cardBackTotalPrice + envelopeTotalPrice + totalDeliveryPriceInMinorUnits;
  }

  const minorUnitsToDollars = (price: number, { canShowFreeLabel }: { canShowFreeLabel: boolean }) => {
    const priceInMajorUnits = _minorToMajorUnits(price);

    if (canShowFreeLabel && priceInMajorUnits === 0) {
      return 'FREE';
    }

    return currency.formatCurrency(priceInMajorUnits, {
      form: 'auto'
    });
  };

  // Formatted values to be directly interpolated
  return {
    // Card
    pricePerCard: quantityConfig.individualPriceString,
    totalPricePerQuantity: quantityConfig.totalPriceString,
    totalPricePerCardShape: dieCutConfig.getTotalPriceStringByQuantity(quantity) || 'FREE',
    totalPaperTypePrice: minorUnitsToDollars(totalPaperTypePriceInMinorUnits, { canShowFreeLabel: true }),
    totalPaperSizePrice: minorUnitsToDollars(totalPaperSizePriceInMinorUnits, { canShowFreeLabel: true }),

    // Back
    totalBackLayoutPrice: minorUnitsToDollars(totalBackLayoutPriceInMinorUnits, { canShowFreeLabel: true }),

    // Envelope
    totalEnvelopesPrice: minorUnitsToDollars(totalEnvelopesPriceInMinorUnits, { canShowFreeLabel: true }),
    pricePerReturnAddress: returnAddressConfig.individualPriceString ? `${returnAddressConfig.individualPriceString} each` : '',
    totalReturnAddressPrice: returnAddressConfig.getTotalPriceStringByQuantity(quantity),

    // Shipping
    totalDeliveryPrice: totalDeliveryPriceInMinorUnits ? minorUnitsToDollars(totalDeliveryPriceInMinorUnits, { canShowFreeLabel: false }) : undefined,

    total: minorUnitsToDollars(runningTotalInMinorUnits, { canShowFreeLabel: false }),
    totalInMinorUnits: runningTotalInMinorUnits
  };
};

export default useCalculatePrintOrderPrice;
